import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
// import Skeleton from 'react-loading-skeleton'
import { useInfiniteQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import * as spriteIcons from 'assets/svg/sprite'
import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import { useModalContext } from 'context/modalContext'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import ModalWrapper from 'features/ModalWrapper/ModalWrapper'
import { VaultCardGrouped } from 'pages/vault/components/VaultCardGrouped'
import { getAllVaultMediaUnshared } from 'services/endpoints/vault'
import { useUserProfile } from 'store'
import { useProfileCacheStore } from 'store/profileCacheStore'
import { useVaultChatCacheStore } from 'store/vaultChatCacheStore'

import style from './_shared_media_modal.module.scss'
import SelectCategoriesModal from '../SelectCategoriesModal/SelectCategoriesModal'
import SortModal from '../Sort/SortModal'

/* ----------------------------- HOISTED CONSTANTS ---------------------------- */

const FEED_FILTER = {
  first_section: [
    { value: 'group_by_message', name: 'Group By Message', isToggle: true },
    { value: 'unreleased_only', name: 'Unreleased Only', isToggle: true },
  ],
  third_section: [
    { value: 'all_media', name: 'All Media' },
    { value: 'photos', name: 'Photos' },
    { value: 'videos', name: 'Videos' },
  ],
  fourth_section: [
    { value: 'free', name: 'Show Free' },
    { value: 'premium', name: 'Show Premium' },
    { value: 'free_premium', name: 'Show Free & Premium' },
  ],
}

const FEED_SORT = {
  first_section: [
    { value: 'most_recent', name: 'Published Date' },
    { value: 'conversion_rate', name: 'Conversion Rate' },
    { value: 'completion_rate', name: 'Video Completion Rate' },
    { value: 'most_purchased', name: 'Most Purchased' },
    { value: 'most_viewed', name: 'Most Viewed' },
  ],
  second_section: [
    { value: 'asc', name: 'Ascending' },
    { value: 'desc', name: 'Descending' },
  ],
}

const isLocalSortKey = (s?: string) =>
  ['conversion_rate', 'completion_rate', 'most_purchased'].includes((s ?? '').toLowerCase())

const isGroupedNow = (g: boolean | string) =>
  typeof g === 'boolean' ? g : (g === 'true' || g === 'group_by_message')

const getRowPhotos = (row: any) =>
  row?.message?.message?.photos ?? row?.post?.photos ?? []

const getRowVideos = (row: any) =>
  row?.message?.message?.videos ?? row?.post?.videos ?? []

const getGroupedPurchases = (row: any): number =>
  Number(row?.message?.statistics?.purchases ?? 0)

const getGroupedCompletionRate = (row: any): number => {
  const vids = row?.message?.message?.videos
  if (!Array.isArray(vids) || vids.length === 0) return 0
  let max = 0
  for (const v of vids) {
    const cr = Number(v?.completion_rate ?? 0)
    if (cr > max) max = cr
  }
  return max
}

const getItemKey = (row: any, grouped: boolean | string) => {
  const groupedNow = isGroupedNow(grouped)
  const baseId = row?.id ?? row?.message?.message?.id ?? row?.post?.id ?? row?.story?.id
  if (groupedNow) return `group-${baseId ?? Math.random()}`
  const vid = row?.message?.message?.videos?.[0]?.id
  const pid = row?.message?.message?.photos?.[0]?.id
  if (vid != null) return `msg-${baseId}-vid-${vid}`
  if (pid != null) return `msg-${baseId}-pid-${pid}`
  return `row-${baseId ?? Math.random()}`
}

/* -------------------------------------------------------------------------- */

const SharedMediaModalUnshared: FC<{
  submitFn?: (val: any) => void
  user?: any
  selected?: any
  userMedia?: any
  checkingData?: any
  setPurchaseMsgId?: any
  setConfirmBuyModalOpen?: any
  setSelectedMessageBuyPrice?: any
  setMessageId?: (id: string | null) => void
  sharedWith?: string
  setPrice?: (price: number | null) => void
  onClose?: () => void
}> = ({
  submitFn,
  user,
  userMedia,
  checkingData,
  setPurchaseMsgId,
  setConfirmBuyModalOpen,
  setSelectedMessageBuyPrice,
  setMessageId,
  setPrice,
  onClose
}) => {
  const modalData = useModalContext()
  const fallbackUser = useUserProfile()
  const viewerId = fallbackUser?.id ?? ''
  const profileCache = useProfileCacheStore(state =>
    fallbackUser?.username ? state.getProfile(fallbackUser.username, viewerId) : undefined
  )
  const userData = profileCache ?? fallbackUser
  const { id } = useParams<any>()
  const { t } = useTranslation()

  /* ------------------------------- UI state ------------------------------- */

  const [activeFilter, setActiveFilter] = useState<'all_media' | 'photos' | 'videos'>('all_media')
  const [allSelectedMedia, setAllSelectedMedia] = useState<any[]>([])
  const [selectedPosts, setSelecetedPosts] = useState<string[]>([])
  const [allMediaLength, setAllMediaLength] = useState<number>(0)
  const [selectedVariantKeys, setSelectedVariantKeys] = useState<string[]>([])

  const [postsSort, setPostsSort] = useState('')
  const [postsAsc, setPostsAsc] = useState<'asc' | 'desc' | string>('desc')
  const [sortModalOpen, setSortModalOpen] = useState(false)
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false)
  const [filterModalOpen, setFilterModalOpen] = useState(false)

  const [activeCategory, setActiveCategory] = useState<any>([{
    name: id ? 'Loading...' : 'All',
    id: id ?? 0,
    posts: 0,
    premium: 0,
    stories: 0,
    messages: 0,
    selected: true
  }])

  const [postsGrouped, setPostsGrouped] = useState<boolean>(true)

  // free/premium
  const [filterOption, setFilterOption] = useState<'free' | 'premium' | 'free_premium'>('free_premium')

  // unreleased-only (frontend filter)
  const [unreleasedOnly, setUnreleasedOnly] = useState<boolean>(false)

  const type = 'messages'
  const sharedMediaUser = Boolean(userMedia && window.location.pathname.includes('/chat'))
  const [userSort, setUserSort] = useState<{ sort: string; order: string }>({ sort: 'most_recent', order: 'desc' })

  const [selectRemovedMedia] = useState<boolean>(false)
const [serverCategoryMode, setServerCategoryMode] = useState<'all' | 'suggested'>('all')

  const { getVaultMedia, setVaultMedia } = useVaultChatCacheStore()

  /* -------------------------------- helpers ------------------------------- */

  const headerButtons = useMemo(() => ([
    { type: 'sort',   customClickFn: () => setSortModalOpen(true)  },
    { type: 'filter', customClickFn: () => setFilterModalOpen(true) }
  ]), [])

  /* ---------------------------- derive query key --------------------------- */

  const queryKeyParams = useMemo(() => {
    if (!userMedia?.id || !activeCategory) return null

    return {
      type,
      media_type: activeFilter,
      postsSort,
      postsAsc,
      postsGrouped,
      filterOption,
      selectRemovedMedia,
      userId: user?.id,
      targetUserId: userMedia.id,
    }
  }, [
    type,
    activeFilter,
    postsSort,
    postsAsc,
    postsGrouped,
    filterOption,
    selectRemovedMedia,
    user?.id,
    userMedia?.id
  ])

  const isReadyToQuery = useMemo(
    () => Boolean(queryKeyParams),
    [queryKeyParams]
  )

const normalizedKeyParams = useMemo(() => {
  if (!queryKeyParams) return null

  const {
    type,
    media_type,
    postsSort,
    postsAsc,
    postsGrouped,
    filterOption,
    selectRemovedMedia,
    targetUserId
  } = queryKeyParams

  const key: any = {
    type,
    media_type,
    postsGrouped,
    filterOption,
    selectRemovedMedia,
    targetUserId,
    category_key: serverCategoryMode,
  }

  const localSort = (postsSort || '').toLowerCase()

  if (!isLocalSortKey(localSort)) {
    if (postsSort) key.postsSort = postsSort
    if (postsAsc) key.postsAsc = postsAsc
  }

  return key
}, [queryKeyParams, serverCategoryMode])


  const stableQueryKey = useMemo(() => ['vaultMedia', normalizedKeyParams], [normalizedKeyParams])
  const vaultCacheKey = useMemo(() => JSON.stringify(normalizedKeyParams), [normalizedKeyParams])

  /* ---------------------------- category bootstrap ---------------------------- */

  useEffect(() => {
    const categories = userData?.all_media_categories
    if (!categories) return
    if (id) {
      const selected = categories.find((category: any) => category.id === parseFloat(id))
      setActiveCategory(selected ? [selected] : [])
    } else {
      setActiveCategory([{ name: 'All', id: 0, posts: 0, premium: 0, stories: 0, messages: 0, selected: true }])
    }
  }, [userData?.all_media_categories, id])

  /* --------------------------------- cache --------------------------------- */



  /* --------------------------------- query --------------------------------- */

const queryFn = useCallback(async ({ pageParam = 0 }) => {
  if (!queryKeyParams) return { data: [], nextPage: undefined }

  const groupedFlag = isGroupedNow(queryKeyParams.postsGrouped)

  const params: any = {
    grouped: groupedFlag,
    mapExistingPostId: false,
    offline: queryKeyParams.selectRemovedMedia,
    unshared_with: queryKeyParams.targetUserId,
    media_type: queryKeyParams.media_type,
    free_premium_filter: queryKeyParams.filterOption,
  }

  const localSort = (queryKeyParams.postsSort || '').toLowerCase()

  if (!isLocalSortKey(localSort)) {
    if (queryKeyParams.postsSort) params.sort = queryKeyParams.postsSort
    if (queryKeyParams.postsAsc) params.order = queryKeyParams.postsAsc
  }

  if (serverCategoryMode === 'suggested') {
    params.category = 'suggested'
  }

  return await getAllVaultMediaUnshared(queryKeyParams.type, pageParam, params)
}, [queryKeyParams, serverCategoryMode])



const {
  data,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  // isFetching, // optional: can be useful for a tiny top-bar spinner instead of skeleton
} = useInfiniteQuery(
  stableQueryKey,
  queryFn,
  {
    enabled: isReadyToQuery,
    staleTime: 0,        // 5m: stay fresh to avoid refetch flicker
    cacheTime: 900000,        // 15m in cache
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
    select: (res) => {
      const flat = (res?.pages ?? []).flatMap((p: any) => p?.data ?? [])
      return { ...res, flat } satisfies typeof res & { flat: any[] }
    },
initialData: () => {
  const cached = getVaultMedia(vaultCacheKey)
  if (!cached) return undefined
  // v3 InfiniteData shape
  return {
    pageParams: [0],
    pages: [{ data: cached, nextPage: undefined }],
  } as any
},
    onSuccess: (res) => {
      // keep your external cache in sync (optional, but keeps the local cache “warm”)
      const flat = (res?.pages ?? []).flatMap((p: any) => p?.data ?? [])
      setVaultMedia(vaultCacheKey, flat)
    },
    onError: (error) => {
      console.error('[Query] Error:', error, '| user:', userMedia?.username)
    }
  }
)

const showInitialLoader = isLoading && !getVaultMedia(vaultCacheKey)

const media = useMemo<any[]>(
  () => (data?.pages ?? []).flatMap((p: any) => p?.data ?? []),
  [data]
)

  /* --------------------------- profile self-hydrate -------------------------- */

  const setProfile = useProfileCacheStore(state => state.setProfile)

  useEffect(() => {
    if (!profileCache && fallbackUser?.username) {
      setProfile(fallbackUser.username, fallbackUser, viewerId)
    }
  }, [profileCache, fallbackUser, viewerId, setProfile])

  /* ----------------------------- infinite scroll ---------------------------- */

  const observer = useRef<IntersectionObserver>()
  const lastPostRef = useCallback(
    (node: any) => {
      if (isFetchingNextPage) return
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage()
          }
        },
        { rootMargin: '150px' }
      )
      if (node) observer.current.observe(node)
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage]
  )

  /* ------------------------------- selection ------------------------------- */

  const handleSelectVariant = useCallback((variant: any) => {
    const key = String(variant?.__k ?? '')
    if (!key) return

    const alreadySelected = selectedVariantKeys.includes(key)
    const delta = alreadySelected ? -1 : +1

    if (!alreadySelected && allMediaLength + delta > 40) {
      addToast('error', 'Only 40 media files can be selected at once')
      return
    }

    setSelectedVariantKeys(prev => (alreadySelected ? prev.filter(k => k !== key) : [...prev, key]))

    setAllSelectedMedia(prev =>
      prev.some((x: any) => x?.__k === key)
        ? prev.filter((x: any) => x?.__k !== key)
        : [...prev, variant]
    )

    setAllMediaLength(prev => prev + delta)
  }, [selectedVariantKeys, allMediaLength])


  const toggleSelectedPosts = useCallback((val: any) => {
    const isGrouped = !!postsGrouped

    if (!isGrouped) {
      handleSelectVariant(val)
      return
    }

    const rawGroupId =
      val?.id ??
      val?.message?.message?.id ??
      val?.post?.id ??
      val?.story?.id

    if (rawGroupId == null) return

    const groupId = String(rawGroupId)

    let calcNumber = 0

    if (val?.post) {
      const pv = Array.isArray(val.post.videos) ? val.post.videos.length : 0
      const pp = Array.isArray(val.post.photos) ? val.post.photos.length : 0
      calcNumber = pv + pp
    }

    if (val?.message?.message) {
      const mv = Array.isArray(val.message.message.videos)
        ? val.message.message.videos.length
        : 0

      const mp = Array.isArray(val.message.message.photos)
        ? val.message.message.photos.length
        : 0

      calcNumber = Math.max(calcNumber, mv + mp)
    }

    if (val?.story) calcNumber = 1

    const willAdd = !selectedPosts.includes(groupId)

    if (willAdd && allMediaLength + calcNumber > 40) {
      addToast('error', 'Only 40 media files can be selected at once')
      return
    }

    setSelecetedPosts(prev =>
      prev.includes(groupId)
        ? prev.filter(x => x !== groupId)
        : [...prev, groupId]
    )

    setAllSelectedMedia(prev => {
      const exists = prev.some((x: any) => {
        const xId = String(
          x?.id ??
          x?.message?.message?.id ??
          x?.post?.id ??
          x?.story?.id ??
          ''
        )

        return xId === groupId
      })

      if (exists) {
        setAllMediaLength(n => Math.max(0, n - calcNumber))

        return prev.filter((x: any) => {
          const xId = String(
            x?.id ??
            x?.message?.message?.id ??
            x?.post?.id ??
            x?.story?.id ??
            ''
          )

          return xId !== groupId
        })
      }

      setAllMediaLength(n => n + calcNumber)
      return [...prev, val]
    })
  }, [postsGrouped, selectedPosts, allMediaLength, handleSelectVariant])

  /* ------------------------- reset selection on flips ------------------------ */

  const didInit = useRef(false)
  useEffect(() => {
    if (didInit.current) {
      setAllSelectedMedia([])
      setSelecetedPosts([])
      setAllMediaLength(0)
      setSelectedVariantKeys([])
    } else {
      didInit.current = true
    }
  }, [activeFilter, postsGrouped])

  /* ----------------------- local sort + display list ------------------------ */
const displayMedia = useMemo(() => {
  const sortKey = (postsSort || '').toLowerCase()
  const grouped = isGroupedNow(postsGrouped)
  const ascMul = (postsAsc || 'desc').toLowerCase() === 'asc' ? 1 : -1

  // fast path: no local sort → return media as-is
  if (!['conversion_rate', 'completion_rate', 'most_purchased'].includes(sortKey)) {
    return media
  }

  if (grouped) {
    const list = media.slice()
    list.sort((a, b) => {
      const av = sortKey === 'conversion_rate'
        ? Number(a?.message?.statistics?.conversion_rate ?? 0)
        : sortKey === 'completion_rate'
          ? getGroupedCompletionRate(a)
          : getGroupedPurchases(a)
      const bv = sortKey === 'conversion_rate'
        ? Number(b?.message?.statistics?.conversion_rate ?? 0)
        : sortKey === 'completion_rate'
          ? getGroupedCompletionRate(b)
          : getGroupedPurchases(b)
      return (av - bv) * ascMul
    })
    return list
  }

  // UNGROUPED: build a light “variant view” array without deep cloning everything
  const takePhotos = activeFilter === 'all_media' || activeFilter === 'photos'
  const takeVideos = activeFilter === 'all_media' || activeFilter === 'videos'

  type VariantView = { row: any; kind: 'photo' | 'video'; item: any; metric: number }
  const out: VariantView[] = []

  for (const row of media) {
    if (takePhotos) {
      for (const p of getRowPhotos(row)) {
        const metric = sortKey === 'conversion_rate' ? Number(p?.conversion_rate ?? 0)
                    : sortKey === 'completion_rate' ? 0
                    : Number(p?.purchases ?? 0)
        out.push({ row, kind: 'photo', item: p, metric })
      }
    }
    if (takeVideos) {
      for (const v of getRowVideos(row)) {
        const metric = sortKey === 'conversion_rate' ? Number(v?.conversion_rate ?? 0)
                    : sortKey === 'completion_rate' ? Number(v?.completion_rate ?? 0)
                    : Number(v?.purchases ?? 0)
        out.push({ row, kind: 'video', item: v, metric })
      }
    }
  }

  out.sort((a, b) => (a.metric - b.metric) * ascMul)

  // rehydrate the minimal row needed by your <VaultCardGrouped />
  return out.map(({ row, kind, item }) => {
    if (row?.message?.message) {
      return { ...row, message: { ...row.message, message: { ...row.message.message, photos: kind === 'photo' ? [item] : [], videos: kind === 'video' ? [item] : [] } } }
    }
    if (row?.post) {
      return { ...row, post: { ...row.post, photos: kind === 'photo' ? [item] : [], videos: kind === 'video' ? [item] : [] } }
    }
    if (row?.story) {
      return { ...row, story: { ...row.story } }
    }
    return row
  })

  
}, [media, postsSort, postsAsc, postsGrouped, activeFilter])


  /* --------------------------------- submit -------------------------------- */

const submitMedia = useCallback(async (): Promise<void> => {
  if (!submitFn) return

  if (setMessageId) {
    const prevIds = allSelectedMedia
      .flatMap((item: any) => {
        const ids =
          item?.message?.previous_message_ids ??
          item?.message?.previuos_message_ids ??
          null

        if (Array.isArray(ids)) return ids

        return typeof ids === 'string' ? [ids] : []
      })
      .filter((x: unknown): x is string => typeof x === 'string')

    setMessageId(prevIds[0] ?? null)
  }

  if (setPrice && allSelectedMedia.length > 0) {
    const mediaWithPrice = allSelectedMedia.find(
      (item: any) => item?.message?.message?.price != null
    )

    if (mediaWithPrice?.message?.message?.price != null) {
      setPrice(mediaWithPrice.message.message.price)
    }
  }

  submitFn(allSelectedMedia)
  onClose?.()
  modalData.clearModal()
}, [submitFn, allSelectedMedia, setMessageId, setPrice, modalData])



//



// Extract category IDs from any row shape we care about.
// Supports arrays like: number[], {id:number}[], {category_id:number}[], or mixed.
const toIds = (arr: any[]): number[] => {
  if (!Array.isArray(arr)) return []
  const ids = arr
    .map((c: any) => {
      if (typeof c === 'number') return c
      if (c && typeof c === 'object') {
        if (Number.isFinite(c.id)) return Number(c.id)
        if (Number.isFinite(c.category_id)) return Number(c.category_id)
      }
      return NaN
    })
    .filter((n: any) => Number.isFinite(n))
  return Array.from(new Set(ids))
}

const getRowCategoryIds = (row: any): number[] => {
  // primary: API shows media_category at message.message level in your sample
  const msgCats = toIds(row?.message?.message?.media_category || row?.message?.message?.categories || [])

  // also check post/story if present in other datasets
  const postCats  = toIds(row?.post?.media_category  || row?.post?.categories  || [])
  const storyCats = toIds(row?.story?.media_category || row?.story?.categories || [])

  // variant-level (photos/videos) — if backend annotates them separately
  const photoCats = [
    ...(row?.message?.message?.photos  || []),
    ...(row?.post?.photos              || []),
    ...(row?.story?.photos             || []),
  ].flatMap((p: any) => toIds(p?.media_category || p?.categories || []))

  const videoCats = [
    ...(row?.message?.message?.videos || []),
    ...(row?.post?.videos             || []),
    ...(row?.story?.videos            || []),
  ].flatMap((v: any) => toIds(v?.media_category || v?.categories || []))

  return Array.from(new Set([...msgCats, ...postCats, ...storyCats, ...photoCats, ...videoCats]))
}


// QUERY FOR SUGGESTED

function getActiveCategory(): any | null {
  return Array.isArray(activeCategory) ? activeCategory[0] : null;
}

function getActiveCategoryNumericId(): number | null {
  const c = getActiveCategory();
  const n = Number((c)?.id);
  return Number.isFinite(n) ? n : null;
}

// For client-side gating (used by applyRowFilters)
function getActiveCategoryId(): number | null {
  return getActiveCategoryNumericId();
}


  /* ---------------------------------- UI ---------------------------------- */

const applyRowFilters = useCallback((item: any) => {
  const activeCatId = getActiveCategoryId()
  if (activeCatId && activeCatId !== 0) {
    const rowCatIds = getRowCategoryIds(item)
    if (!rowCatIds.includes(activeCatId)) return false
  }

  if (item?.story && !(item?.story?.video_src || item?.story?.image_src)) return false
  if (filterOption === 'premium' && item?.message?.message?.price === 0) return false
  if (filterOption === 'free'    && item?.message?.message?.price !== 0) return false

  if (unreleasedOnly) {
    const groupedNow = isGroupedNow(postsGrouped)
    const views = groupedNow ? Number(item?.message?.statistics?.views ?? 0)
                             : Number(item?.message?.message?.views ?? 0)
    if (views !== 0) return false
  }

  if (sharedMediaUser && item?.message?.message_send_users?.length <= 2) {
    return userMedia.role === 'model'
      ? item?.message?.users[0]?.id !== userMedia?.id
      : item?.message?.users[1]?.id !== userMedia?.id
  }

  if (sharedMediaUser && item.vaultable?.mass_message_id != null) {
    const hasSeenUsers = item?.message?.message_seen_users?.filter((i: { user_id: any; is_seen: number }) => i?.is_seen === 1)
    if (hasSeenUsers?.some((u: any) => u.user_id === userMedia?.id)) return false
  }

  if (item?.message === null && item?.post === null && item?.story === null) return false
  if (item?.message !== null && item?.message?.message?.deleted_at !== null) return false

  return true
}, [filterOption, unreleasedOnly, postsGrouped, sharedMediaUser, userMedia?.id, userMedia?.role, activeCategory])

// make sure switching category clears selection too
useEffect(() => {
  setAllSelectedMedia([])
  setSelecetedPosts([])
  setAllMediaLength(0)
  setSelectedVariantKeys([])
}, [activeFilter, postsGrouped, activeCategory])



  const badgeMode = useMemo<'completion' | undefined>(() => {
    return (postsSort || '').toLowerCase() === 'completion_rate' ? 'completion' : undefined
  }, [postsSort])

  return (
    <div className={style.container}>
      <div className={style.title}>
        <div className={style.title_with_user_inline}>
          <span className={style.unshared_label}>Unshared With</span>
          <div className={style.avatar_inline}>
            <img src={userMedia?.avatar?.url || avatarPlaceholder} alt='avatar' />
          </div>
          <span className={style.username_inline}>
            @{userMedia?.username || ''}
          </span>
        </div>
      </div>

      <div className={style.title_container}>
        {userData?.role === 'model' && !user && (
          <div className={style.withHeaderSectionView}>
            <div />
            <div className={style.sort_buttons_container} />
          </div>
        )}
      </div>

      {userMedia && userData?.role === 'model' && window.location.pathname.includes('/chat') && (
        <div className={style.withHeaderSectionView}>
          <LayoutHeader
            type='title-with-buttons'
            buttons={headerButtons}
            titleWithIcon={
              <div
                className={style.select_category_container}
                onClick={() => setCategoriesModalOpen(true)}
              >
                <div className={style.select_category_icon}>
                  <spriteIcons.IconList color='#ffffff' width='14' height='14' />
                </div>
                <div className={style.select_category_text}>
                  {activeCategory[0]?.name}
                </div>
              </div>
            }
          />

          <div>
            <ModalWrapper open={categoriesModalOpen} setOpen={setCategoriesModalOpen}>
              <SelectCategoriesModal
                title="Select a Media Category"
                onlyOne={true}
                hidePublicCategories={true}
                hideVisibilityBadges={true}
                selectedCategories={activeCategory}
                onSave={(val: any) => {
                  setActiveFilter('all_media');

                  // decide the committed category list
                  if (val.length > 0 && val[0].id) {
                    setActiveCategory(val);
                  } else {
                    setActiveCategory([{
                      name: 'All',
                      id: 0,
                      posts: 0,
                      premium: 0,
                      stories: 0,
                      messages: 0,
                      selected: true
                    }]);
                  }

                  // flip server mode ONLY on Save
                  const first = val[0];
                  if (first && String(first.id) === 'suggested') {
                    setServerCategoryMode('suggested');
                  } else {
                    setServerCategoryMode('all');
                  }

                  setCategoriesModalOpen(false);
                }}

                includeSuggestedCategory={true}
                onCancel={() => setCategoriesModalOpen(false)}
                dontClearModal={true}
                chatUserId={userMedia?.id}
              />
            </ModalWrapper>
          </div>

          <div className={style.sort_buttons_container}>
            <ModalWrapper open={sortModalOpen} setOpen={setSortModalOpen}>
              <div className={style.sort_modal_container}>
                <div className={style.title}>{t('Sort')}</div>
                <SortModal
                  applyFn={(val1: string, val2: string) => {
                    if (!val1.trim() || !val2.trim()) {
                      setPostsSort('')
                      setPostsAsc('')
                      setUserSort({ sort: 'most_recent', order: 'desc' })
                      setSortModalOpen(false)
                      return
                    }
                    setPostsSort(val1)
                    setPostsAsc(val2)
                    setUserSort({ sort: val1, order: val2 })
                    setSortModalOpen(false)
                  }}
                  sortingProps={{
                    selectedSort: postsSort || userSort.sort,
                    selectedOrder: postsAsc || userSort.order,
                  }}
                  dontClearModal={true}
                  elements={FEED_SORT}
                />
              </div>
            </ModalWrapper>

            <ModalWrapper open={filterModalOpen} setOpen={setFilterModalOpen}>
              <div className={style.sort_modal_container}>
                <div className={style.title}>{t('Filter')}</div>
                <SortModal
                  applyFn={(
                    val1: string | boolean | undefined,
                    val2?: 'all_media' | 'photos' | 'videos',
                    _selectProps?: any,
                    _showRemoved?: boolean,
                    fourthVal?: 'free' | 'premium' | 'free_premium',
                    unreleasedFlag?: boolean
                  ) => {
                    if (typeof val1 !== 'undefined') {
                      setPostsGrouped(val1 === true || val1 === 'true' || val1 === 'group_by_message')
                    }
                    if (typeof unreleasedFlag !== 'undefined') setUnreleasedOnly(!!unreleasedFlag)
                    if (typeof val2 === 'string' && val2.length > 0) setActiveFilter(val2)
                    if (typeof fourthVal !== 'undefined') setFilterOption(fourthVal)
                    setFilterModalOpen(false)
                  }}
                  sortingProps={{
                    selectedSort: postsGrouped ? 'group_by_message' : 'off',
                    selectedOrder: activeFilter || 'all_media',
                    filterOption: filterOption || 'free_premium',
                    unreleasedOnly,
                  } as any}
                  dontClearModal={true}
                  elements={FEED_FILTER}
                />
              </div>
            </ModalWrapper>
          </div>
        </div>
      )}

<div className={style.cards_container}>
  {showInitialLoader ? (
    <div className={style.media_loader}>
      <BeatLoader size={8} />
    </div>
  ) : (
    displayMedia
      .filter(applyRowFilters)
      .map((m: any) => {
        const mediaId = String(
          m?.id ??
          m?.message?.message?.id ??
          m?.post?.id ??
          m?.story?.id ??
          ''
        )

        return (
          <VaultCardGrouped
            onCirlceClick={toggleSelectedPosts}
            onSelectVariant={handleSelectVariant}
            isVariantSelected={(key: string) =>
              selectedVariantKeys.includes(key)
            }
            getVariantIndex={(key: string) => {
              const i = selectedVariantKeys.indexOf(key)
              return i >= 0 ? i + 1 : null
            }}
            media={m}
            selected={selectedPosts.includes(mediaId)}
            selectedText={selectedPosts.indexOf(mediaId) + 1}
            grouped={postsGrouped}
            key={getItemKey(m, postsGrouped)}
            customClass={style.mobileView}
            customCirlce={style.customCirlce}
            userMedia={userMedia}
            checkingData={checkingData}
            type={type}
            setPurchaseMsgId={setPurchaseMsgId}
            setConfirmBuyModalOpen={setConfirmBuyModalOpen}
            setSelectedMessageBuyPrice={setSelectedMessageBuyPrice}
            badgeMode={badgeMode}
          />
        )
      })
  )}

  {!showInitialLoader && hasNextPage && (
    <div style={{ width: '100%' }} ref={lastPostRef}>
      <div className='loader' />
    </div>
  )}
</div>

      <div className={style.buttons_container}>
        <Button color='grey' text={t('close')}   clickFn={() => {
    onClose?.()
    modalData.clearModal()
  }} />
        <Button color='black' text={t(user ? 'resend' : 'addMedia')} clickFn={submitMedia} />
      </div>
    </div>
  )
}

export default SharedMediaModalUnshared
